<template>
  <div class="tiptap-wrapper" :class="editorClass">
    <div v-if="editor" class="tiptap-toolbar">
      <!-- Text style -->
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('bold') }"
        title="Bold"
        @click="editor.chain().focus().toggleBold().run()"
      ><strong>B</strong></button>
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('italic') }"
        title="Italic"
        @click="editor.chain().focus().toggleItalic().run()"
      ><em>I</em></button>
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('strike') }"
        title="Strikethrough"
        @click="editor.chain().focus().toggleStrike().run()"
      ><s>S</s></button>

      <span class="tiptap-divider"></span>

      <!-- Headings -->
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
        title="Heading 1"
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      >H1</button>
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
        title="Heading 2"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >H2</button>
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
        title="Heading 3"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      >H3</button>

      <span class="tiptap-divider"></span>

      <!-- Lists -->
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        title="Bullet List"
        @click="editor.chain().focus().toggleBulletList().run()"
      >&#8226;&#8801;</button>
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        title="Ordered List"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >1&#8801;</button>

      <span class="tiptap-divider"></span>

      <!-- Blocks -->
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('blockquote') }"
        title="Blockquote"
        @click="editor.chain().focus().toggleBlockquote().run()"
      >&#8220;&#8221;</button>
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('code') }"
        title="Inline Code"
        @click="editor.chain().focus().toggleCode().run()"
      >&lt;/&gt;</button>
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('codeBlock') }"
        title="Code Block"
        @click="editor.chain().focus().toggleCodeBlock().run()"
      >&#91;&#32;&#93;</button>

      <span class="tiptap-divider"></span>

      <!-- Link -->
      <button
        type="button"
        class="tiptap-btn"
        :class="{ 'is-active': editor.isActive('link') }"
        title="Insert / Edit Link"
        @click="setLink"
      >&#128279;</button>
      <button
        v-if="editor.isActive('link')"
        type="button"
        class="tiptap-btn"
        title="Remove Link"
        @click="editor.chain().focus().unsetLink().run()"
      >&#128278;</button>

      <span class="tiptap-divider"></span>

      <!-- History -->
      <button
        type="button"
        class="tiptap-btn"
        title="Undo"
        :disabled="!editor.can().undo()"
        @click="editor.chain().focus().undo().run()"
      >&#8617;</button>
      <button
        type="button"
        class="tiptap-btn"
        title="Redo"
        :disabled="!editor.can().redo()"
        @click="editor.chain().focus().redo().run()"
      >&#8618;</button>

      <span class="tiptap-divider"></span>

      <!-- Clear formatting -->
      <button
        type="button"
        class="tiptap-btn"
        title="Clear Formatting"
        @click="editor.chain().focus().clearNodes().unsetAllMarks().run()"
      >&#10005;</button>
    </div>
    <editor-content :editor="editor" />
  </div>
</template>

<script>
import { watch, onBeforeUnmount } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

export default {
  name: 'TiptapEditor',
  components: {
    EditorContent
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    editorClass: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const editor = useEditor({
      content: props.modelValue || '',
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            rel: 'noopener noreferrer',
            target: '_blank'
          }
        })
      ],
      editorProps: {
        attributes: {
          class: 'tiptap-editor',
          placeholder: props.placeholder
        }
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        emit('update:modelValue', html);
      }
    });

    watch(
      () => props.modelValue,
      (newVal) => {
        if (!editor.value) {
          return;
        }
        const currentHTML = editor.value.getHTML();
        if (currentHTML !== newVal) {
          editor.value.commands.setContent(newVal || '', false);
        }
      }
    );

    onBeforeUnmount(() => {
      if (editor.value) {
        editor.value.destroy();
      }
    });

    const setLink = () => {
      const previousUrl = editor.value.getAttributes('link').href || '';
      const url = window.prompt('Enter URL', previousUrl);
      if (url === null) return; // cancelled
      if (url === '') {
        editor.value.chain().focus().unsetLink().run();
        return;
      }
      // Restrict to safe protocols only
      const safeProtocol = /^(https?:\/\/|mailto:)/i;
      const sanitizedUrl = safeProtocol.test(url) ? url : `https://${url}`;
      editor.value.chain().focus().setLink({ href: sanitizedUrl }).run();
    };

    return { editor, setLink };
  }
};
</script>

<style>
/* Wrapper */
.tiptap-wrapper {
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

/* Toolbar */
.tiptap-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background-color: #f8f8f8;
  border-bottom: 1px solid #ccc;
}

.tiptap-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 5px;
  border: 1px solid transparent;
  border-radius: 3px;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: #444;
  transition: background 0.15s, border-color 0.15s;
}

.tiptap-btn:hover {
  background-color: #e8e8e8;
  border-color: #ccc;
}

.tiptap-btn.is-active {
  background-color: #d0e8ff;
  border-color: #007bff;
  color: #0056b3;
}

.tiptap-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.tiptap-divider {
  display: inline-block;
  width: 1px;
  height: 20px;
  background-color: #ccc;
  margin: 0 4px;
}

/* Editor content area */
.tiptap-editor {
  min-height: 100px;
  padding: 12px;
}

.tiptap-editor:focus {
  outline: none;
}

.ProseMirror {
  min-height: 100px;
}

.ProseMirror:focus {
  outline: none;
}

/* Content styles */
.ProseMirror p {
  margin: 0 0 1em;
}

.ProseMirror h1,
.ProseMirror h2,
.ProseMirror h3,
.ProseMirror h4,
.ProseMirror h5,
.ProseMirror h6 {
  margin: 0 0 0.5em;
  font-weight: bold;
}

.ProseMirror ul,
.ProseMirror ol {
  padding-left: 1.5em;
  margin: 0 0 1em;
}

.ProseMirror blockquote {
  border-left: 3px solid #ccc;
  padding-left: 1em;
  margin: 0 0 1em;
  font-style: italic;
}

.ProseMirror code {
  background-color: #f4f4f4;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
}

.ProseMirror pre {
  background-color: #f4f4f4;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 0 0 1em;
}

.ProseMirror pre code {
  background: none;
  padding: 0;
}

.ProseMirror a {
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
}
</style>
